import { EFolderAssets } from "@/modules/helpers/enum"

const URL_S3 = import.meta.env.VITE_APP_PUBLIC_URL_S3
export const getImageListByNameFromS3 = (
  images: any[],
  userId: string,
  prefix = "campaign_user_id"
) => {
  return images.map((item) => {
    if (!item) return URL_S3
    return URL_S3 + prefix + "_" + userId + "/" + item.name
  })
}

export const getImageByNameFromS3 = (
  name?: string | null,
  userId?: string | number,
  prefix = EFolderAssets.THUMBNAIL_CAMPAIGN
) => {
  // If name is already a full URL (http or https), return it directly
  if (name?.includes("http://") || name?.includes("https://")) {
    return name
  }

  // Handle fallback for AVATAR
  if (prefix === EFolderAssets.AVATAR && (!name || !userId)) {
    return "/favicon-96x96.png"
  }

  // If name or userId is empty/null/undefined for other assets, return empty string
  if (!name || (!userId && prefix !== EFolderAssets.AVATAR)) {
    return ""
  }

  // Construct full S3 URL
  return `${URL_S3}${prefix}_${userId}/${name}`
}

export const getImageByNameFormatV2 = (name?: string | null) => {
  if (name) return URL_S3 + name
  return ""
}

export const onUploadImageToS3 = async (url: string) => {
  // const arrayBuffer = decode(base64);
  return await fetch(url, {
    method: "PUT",
    // body: arrayBuffer,
    headers: {
      "Content-Type": "image/octet-stream"
    }
  })
}

export const onUploadAssetsToS3 = async (url: string, file: File | null | Blob) => {
  return fetch(url, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": "multipart/form-data"
    }
  })
}

export const onUploadAssetsToS3Progress = (
  url: string,
  file: File | null | Blob,
  onProgress: (progress: number) => void
) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open("PUT", url, true)
    xhr.setRequestHeader("Content-Type", "multipart/form-data")

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const progress = (event.loaded / event.total) * 100
        onProgress(progress)
      }
    }

    xhr.onload = () => {
      if (xhr.status === 200) {
        resolve(xhr.response)
      } else {
        // alert(xhr.status)
        reject(new Error(`Upload failed with status ${xhr.status}`))
      }
    }

    xhr.onerror = () => {
      reject(new Error("Network error occurred during upload"))
    }

    xhr.send(file)
  })
}

export const getImageUrlFromS3 = (imageUrl: string) => {
  try {
    const baseUrl = imageUrl.substring(0, imageUrl.indexOf("?"))
    return baseUrl
  } catch {
    return imageUrl
  }
}

/**
 * Sanitize file name by removing special characters, accents, and normalizing
 * @param fileName - Original file name
 * @returns Sanitized file name safe for S3 upload
 */
export const sanitizeFileName = (fileName?: string | null): string => {
  if (!fileName) return ""

  const lastDotIndex = fileName.lastIndexOf(".")
  const namePart = lastDotIndex !== -1 ? fileName.slice(0, lastDotIndex) : fileName
  const extPart = lastDotIndex !== -1 ? fileName.slice(lastDotIndex) : ""

  const safeName = namePart
    .normalize("NFD") // chuẩn hoá để bỏ dấu
    .replace(/[\u0300-\u036f]/g, "") // bỏ dấu tiếng Việt
    .replace(/[^a-zA-Z0-9]/g, "_") // thay ký tự đặc biệt bằng "_"
    .replace(/_+/g, "_") // gộp nhiều "_"
    .replace(/^_+|_+$/g, "") // xoá "_" đầu/cuối

  return `${safeName}${extPart || ".mp4"}`
}

/**
 * Upload a single file to S3
 * @param url - Presigned S3 URL
 * @param file - File to upload
 * @param onProgress - Optional progress callback
 * @returns Promise that resolves when upload is complete
 */
export const uploadFileToS3 = async (
  url: string,
  file: File | Blob | null,
  onProgress?: (progress: number) => void
): Promise<void> => {
  if (!file) {
    throw new Error("File is required for upload")
  }

  if (onProgress) {
    return onUploadAssetsToS3Progress(url, file, onProgress) as Promise<void>
  }

  const response = await onUploadAssetsToS3(url, file)
  if (!response.ok) {
    throw new Error(`Upload failed with status ${response.status}`)
  }
}

/**
 * Upload multiple files to S3 with overall progress tracking
 * @param uploads - Array of { url, file } objects
 * @param onOverallProgress - Callback for overall progress (0-100)
 * @returns Promise that resolves when all uploads are complete
 */
export const uploadMultipleFilesToS3 = async (
  uploads: Array<{ url: string; file: File | Blob | null }>,
  onOverallProgress?: (progress: number) => void
): Promise<void[]> => {
  if (!uploads.length) {
    throw new Error("No files to upload")
  }

  // Filter out invalid files
  const validUploads = uploads.filter((item) => !!item.file && !!item.url)
  if (!validUploads.length) {
    throw new Error("No valid files to upload")
  }

  const progressMap = new Map<number, number>()

  const updateOverallProgress = () => {
    if (!onOverallProgress) return

    const totalProgress =
      Array.from(progressMap.values()).reduce((acc, progress) => acc + progress, 0) /
      validUploads.length

    onOverallProgress(Math.min(100, Math.max(0, totalProgress)))
  }

  // Upload all files in parallel
  const uploadPromises = validUploads.map((item, index) => {
    progressMap.set(index, 0)

    return onUploadAssetsToS3Progress(item.url, item.file!, (progress) => {
      progressMap.set(index, progress)
      updateOverallProgress()
    })
  })

  return Promise.all(uploadPromises) as Promise<void[]>
}

/**
 * Upload files to S3 by mapping files with URLs from API response
 * @param getUrlsFn - Function that returns presigned URLs (should return { data: { data: string[] } })
 * @param files - Array of files to upload
 * @param onOverallProgress - Optional callback for overall progress
 * @returns Promise that resolves when all uploads are complete
 */
export const uploadFilesWithApiUrls = async <T extends { data: { data: string[] } }>(
  getUrlsFn: () => Promise<T>,
  files: Array<File | Blob>,
  onOverallProgress?: (progress: number) => void
): Promise<void[]> => {
  if (!files.length) {
    throw new Error("No files provided")
  }

  // Get presigned URLs from API
  const urlsResponse = await getUrlsFn()
  const urls = urlsResponse?.data?.data

  if (!urls || !Array.isArray(urls) || urls.length !== files.length) {
    throw new Error("Invalid URLs response from API")
  }

  // Map files with URLs
  const uploads = urls.map((url, index) => ({
    url,
    file: files[index]
  }))

  return uploadMultipleFilesToS3(uploads, onOverallProgress)
}

export const getIdFromLinkTiktok = (link: string) => link?.split("https://www.tiktok.com/")?.[1]
